import React, { createContext, useCallback, useContext, useState } from "react";
import { useNotification } from "../notification-context";
import { postAuthenticatedRequest } from "../../../utils/api/requests/request";
import { cleanObject } from "../../../utils/helper/format";
import { downloadFileType } from "../../../shared/constants/constants";
import { downloadFile } from "../../../utils/helper/util";
import {
  getStoredChannelBranch,
  setStoredChannelBranchSilently,
} from "../../../utils/branchSelection";

const LoanRequestContext = createContext();

export const LoanRequestProvider = ({ children }) => {
  const { triggerNotification } = useNotification();
  const [loanRequests, setLoanRequests] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoanAccountByIdLoading, setIsLoanAccountByIdLoading] =
    useState(false);
  const [loanAccountByIdDetails, setLoanAccountByIdDetails] = useState(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  
  // Separate state for header info (shows immediately)
  const [loanHeaderInfo, setLoanHeaderInfo] = useState(null);

  const [filter, setFilter] = useState({
    status: "",
    productId: "",
    searchText: "",
    startDate: "",
    endDate: "",
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectedChannelBranchState, setSelectedChannelBranchState] = useState(
    getStoredChannelBranch()
  );
  const setSelectedChannelBranch = useCallback((branch) => {
    setStoredChannelBranchSilently(branch);
    setSelectedChannelBranchState(branch == null ? null : String(branch));
  }, []);

  const [activeModal, setActiveModal] = useState(null);
  const openModal = (modalName) => {
    setActiveModal(modalName);
  };
  const closeModal = () => {
    setActiveModal(null);
  };

  const getLoanRequests = useCallback(async (extraParams = {}) => {
    try {
      setIsLoading(true);

      // Filter out properties with empty values
      const cleanedFilter = cleanObject({
        ...filter,
        ...pagination,
        ...extraParams,
      });

      console.log("cleanedFilter from loan request", cleanedFilter);

      const response = await postAuthenticatedRequest(
        "GetLoanQuotesWithFiltersQuery",
        cleanedFilter
      );

      const result = response?.outData;
      if (result?.isSuccessful) {
        console.log("loan requests is", result?.data);
        setLoanRequests(result);
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [triggerNotification, filter, pagination]);

  // download loan request report
  const downloadLoanRequestReport = useCallback(async () => {
    try {
      setIsDownloadingReport(true);

      // Filter out properties with empty values
      const cleanedFilter = cleanObject({
        ...filter,
        ...pagination,
        isExport: true,
        fileExportName: "LoanRequestReport",
        exportFields: {
          displayName: "Customer Name",
          requestId: "Request ID",
          productName: "Product Name",
          branchName: "Branch Name",
          loanTenor: "Tenor",
          loanTenorTypeDescription: "Tenor Description",
          currencyCode: "currency",
          loanAmount: "Amount",
          approvalStateDesc: "Status",
          interestRateDisplay: "Interest",
          createdAt: "Application Date",
        },
      });

      const response = await postAuthenticatedRequest(
        "GetLoanQuotesWithFiltersQuery",
        cleanedFilter
      );

      const result = response?.outData;
      console.log("result from download loan requests");
      if (result?.isSuccessful) {
        downloadFile(
          result?.data,
          "Loan_Request_Report",
          downloadFileType.EXCEL
        );
        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Loan request report downloaded",
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: result?.message,
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description: error?.message,
      });
    } finally {
      setIsDownloadingReport(false);
    }
  }, [triggerNotification, filter, pagination]);

  const getLoanAccountById = useCallback(
    async (loanAccountId) => {
      try {
        // Stage 1: Set basic header info immediately from loan ID
        // Don't set loading to true yet - let header render first!
        console.log(`[LOAN PAGE] Stage 1: Setting header with loan ID: ${loanAccountId}`);
        setLoanHeaderInfo({
          accountNumber: loanAccountId,
          customerName: "Loading...",
          isLoading: true,
        });
        
        // Small delay to ensure header renders before showing loading skeleton
        await new Promise(resolve => setTimeout(resolve, 0));
        
        // Stage 2: Now set loading state for content area
        setIsLoanAccountByIdLoading(true);
        
        // Performance monitoring
        const startTime = performance.now();
        console.log(`[LOAN API] Stage 2: Starting RetrieveLoanListQuery for ID: ${loanAccountId}`);
        
        const response = await postAuthenticatedRequest(
          "RetrieveLoanListQuery",
          { id: loanAccountId },
          true
        );
        
        const endTime = performance.now();
        const duration = (endTime - startTime).toFixed(2);
        console.log(`[LOAN API] RetrieveLoanListQuery completed in ${duration}ms`);
        
        const result = response?.outData;
        if (result?.isSuccessful) {
          const loanData = result?.data?.items?.[0];
          console.log(`[LOAN API] Successfully retrieved loan data:`, loanData);
          
          // Update both detailed data and header
          setLoanAccountByIdDetails(loanData);
          setLoanHeaderInfo({
            accountNumber: loanData?.accountNumber || loanAccountId,
            customerName: loanData?.association?.clientName,
            productName: loanData?.association?.productName,
            isLoading: false,
          });
        } else {
          console.error(`[LOAN API] Error retrieving loan:`, result?.message);
          setLoanHeaderInfo({
            accountNumber: loanAccountId,
            isLoading: false,
            error: result?.message,
          });
          triggerNotification({
            type: "error",
            message: "Error",
            description: result?.message,
          });
        }
      } catch (error) {
        console.error(`[LOAN API] Exception in getLoanAccountById:`, error);
        setLoanHeaderInfo({
          accountNumber: loanAccountId,
          isLoading: false,
          error: error?.message,
        });
        triggerNotification({
          type: "error",
          message: "Error",
          description: error?.message,
        });
      } finally {
        setIsLoanAccountByIdLoading(false);
      }
    },
    [triggerNotification]
  );

  return (
    <LoanRequestContext.Provider
      value={{
        loanRequests,
        isLoading,
        isDownloadingReport,
        isLoanAccountByIdLoading,
        loanAccountByIdDetails,
        loanHeaderInfo,
        getLoanAccountById,
        getLoanRequests,
        downloadLoanRequestReport,
        filter,
        setFilter,
        pagination,
        setPagination,
        selectedChannelBranch: selectedChannelBranchState,
        setSelectedChannelBranch,
        activeModal,
        openModal,
        closeModal,
      }}
    >
      {children}
    </LoanRequestContext.Provider>
  );
};

export const useLoanRequest = () => {
  return useContext(LoanRequestContext);
};
